import { computed } from "mobx";
import { inject, observer, Provider } from "mobx-react";
import * as React from "react";
import { CloseButton } from "../Buttons/CloseButton";
import { DeleteButton } from "../Buttons/DeleteButton";
import { DownloadButtonListTiny } from "../Buttons/DownloadButtonListTiny";
import { EditButtonListTiny } from "../Buttons/EditButtonListTiny";
import { FavouriteButtonListTiny } from "../Buttons/FavouriteButtonListTiny";
import { SyncButton } from "../Buttons/SyncButton";
import {
	DocumentsViewEntry,
	Download,
	IDocumentsView,
	handleLoadMore,
	IDocumentsViewEntryPropsBase,
} from "../Document/DocumentsView";
import * as DocumentReader from "../DocumentReader";
import { Area } from "../General/Area";
import { AreaBody } from "../General/AreaBody";
import { FileLink } from "../General/FileLink";
import { Toolbar } from "../General/Toolbar";
import { AppStore } from "../Stores/AppStore";
import { DialogStore } from "../Stores/DialogStore";
import { Strings } from "../Stores/StringsStore";
import { SyncUIStore } from "../Stores/SyncUIStore";
import { Urls } from "../Urls";

@observer
export class Sync extends React.Component<{}, {}> {
	store: SyncUIStore = new SyncUIStore();

	componentDidMount() {
		AppStore.setTitle(Strings.sync.syncTitle);
		this.store.triggerSearch();
	}

	componentDidUpdate() {
		AppStore.setTitle(Strings.sync.syncTitle);
	}

	handleCancel = () => {
		AppStore.keepOnPage(Urls.home);
	};

	handleSync = () => {
		this.store.selectedFiles.forEach((file) => {
			if (
				file.status === DocumentReader.OfflineStatus.Update ||
				file.status === DocumentReader.OfflineStatus.Error
			) {
				Download.requestUpdate(file);
			}
		});
	};

	handleDelete = () => {
		DocumentReader.deleteFilesAsync(
			this.store.selectedFiles.map((file, index) => {
				let query = new DocumentReader.DeleteQuery();
				query.guid = file.guid;
				query.mimeType = file.mimeType;
				return query;
			})
		)
			.then((result: void) => {
				this.store.selectedFiles.forEach((file) => {
					this.store.searchResult.documents.remove(file);
				});
				this.store.selectedFiles.clear();
			})
			.catch((error) => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
	};

	@computed get canSync(): boolean {
		return (
			this.store.selectedFiles.filter(
				(f) =>
					f.status === DocumentReader.OfflineStatus.Update ||
					f.status === DocumentReader.OfflineStatus.Error
			).length > 0
		);
	}

	@computed get canDelete(): boolean {
		return this.store.selectedFiles.length > 0;
	}

	render() {
		return (
			<Provider store={this.store}>
				<Area>
					<AreaBody>
						<Toolbar>
							<SyncButton
								title={Strings.sync.syncButtonLabel}
								onClick={this.handleSync}
								disabled={!this.canSync}
								testId="syncButton"
							/>
							<DeleteButton
								title={Strings.sync.syncDeleteButtonLabel}
								onClick={this.handleDelete}
								disabled={!this.canDelete}
								testId="deleteButton"
							/>
							<span className="buttongroup is--buttongroup-right">
								<CloseButton
									onClick={this.handleCancel}
									testId="closeButton"
								/>
							</span>
						</Toolbar>
						<div className="area-grid__body-content">
							<div className="content-main recent-filelist-container">
								<h1>{Strings.sync.syncTitle}</h1>
								<DocumentList uiStore={this.store} />
							</div>
						</div>
					</AreaBody>
				</Area>
			</Provider>
		);
	}
}

interface IDocumentList {
	store?: IDocumentsView;
	uiStore?: SyncUIStore;
}

@inject("store")
@observer
class DocumentList extends React.Component<IDocumentList, {}> {
	handleLoadMore = (o: any) => {
		handleLoadMore(o, this.props.store);
	};

	render() {
		let body =
			this.props.store.searchResult.documents.length > 0 ? (
				<table
					data-test-id="documentListTable"
					className="is--small-icons"
				>
					<DocumentListHeaders uiStore={this.props.uiStore} />
					<DocumentListBody uiStore={this.props.uiStore} />
				</table>
			) : !this.props.store.isBusy ? (
				<div className="inside__note">
					{this.props.store.noResultMsg}
				</div>
			) : null;

		let spinner = this.props.store.isBusy ? (
			<div className="spinner">
				<div className="spinner-inner">
					<i>
						<svg
							className="spinner-animation"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 48 48"
						>
							<path
								className="spinner-custom"
								d="M24,5A19,19,0,0,1,43,24h3A22,22,0,0,0,24,2Z"
							/>
							<path d="M24,43A19,19,0,0,1,24,5V2A22,22,0,1,0,46,24H43A19,19,0,0,1,24,43Z" />
						</svg>
					</i>
				</div>
				<div className="spinner-label">Loading...</div>
			</div>
		) : null;

		return (
			<div
				className="area-grid__body-content"
				onScroll={this.handleLoadMore}
			>
				<div className="list-advanced">
					{body}
					{spinner}
				</div>
			</div>
		);
	}
}

@inject("store")
@observer
class DocumentListHeaders extends React.Component<IDocumentList, {}> {
	@computed get isSelected(): boolean {
		return this.props.store.searchResult.documents.every(
			(f) => this.props.uiStore.selectedFiles.indexOf(f) > -1
		);
	}

	handleChange = () => {
		if (this.isSelected) {
			this.props.uiStore.selectedFiles.clear();
		} else {
			this.props.uiStore.selectedFiles.push(
				...this.props.store.searchResult.documents.filter(
					(f) => this.props.uiStore.selectedFiles.indexOf(f) === -1
				)
			);
		}
	};

	render() {
		return (
			<thead>
				<tr>
					<th className="short-cell">
						<div data-test-name="documentListHeaderCell">
							<input
								type="checkbox"
								checked={this.isSelected}
								onClick={this.handleChange}
							/>
						</div>
					</th>
					<th className="short-cell fav-cell" />
					<th className="short-cell">
						<span />
					</th>
					<th key="Name">
						<span data-test-name="documentListHeaderCell">
							Name
						</span>
					</th>
					<th className="short-cell" key="Size">
						<span data-test-name="documentListHeaderCell">
							Size
						</span>
					</th>
					{AppStore.context.sessionDetails.isApp ? (
						<th className="short-cell" />
					) : null}
					{AppStore.context.sessionDetails.isDocumentManager ? (
						<th className="short-cell" />
					) : null}
				</tr>
			</thead>
		);
	}
}

@inject("store")
@observer
class DocumentListBody extends React.Component<IDocumentList, {}> {
	render() {
		return (
			<tbody>
				{this.props.store.searchResult.documents.map(
					(document, index) => {
						return (
							<DocumentListRow
								key={document.guid + index}
								fileResult={document}
								uiStore={this.props.uiStore}
								applyUpdatesToModel={true}
							/>
						);
					}
				)}
			</tbody>
		);
	}
}

interface IDocumentListRow
	extends IDocumentsViewEntryPropsBase,
		IDocumentList {}

@inject("store")
@observer
class DocumentListRow extends DocumentsViewEntry<IDocumentListRow> {
	handleChange = () => {
		if (this.isSelected) {
			this.props.uiStore.selectedFiles.remove(this.props.fileResult);
		} else {
			this.props.uiStore.selectedFiles.push(this.props.fileResult);
		}
	};

	@computed get isSelected(): boolean {
		return (
			this.props.uiStore.selectedFiles.indexOf(this.props.fileResult) > -1
		);
	}

	render() {
		return (
			<tr data-test-name="documentListRow">
				<td>
					<label>
						<input
							type="checkbox"
							checked={this.isSelected}
							onClick={this.handleChange}
						/>
					</label>
				</td>
				<td>
					<FavouriteButtonListTiny
						switchState={this.favouriteState}
						onClick={this.handleAddFavourites}
						testName="favouriteButtonMulti"
					/>
				</td>
				<td className="is--cell-small-icon">
					{this.props.fileResult.region.toLowerCase() == "001" ? (
						<img
							title={this.props.fileResult.languageName}
							src={
								"../../images/flags/" +
								this.props.fileResult.region +
								".png"
							}
						/>
					) : (
						<div
							title={this.props.fileResult.languageName}
							className={
								"flag flag-" +
								this.props.fileResult.region.toLowerCase()
							}
						/>
					)}
					-
				</td>
				<td>
					<FileLink
						documentId={this.props.fileResult.documentId}
						language={this.props.fileResult.language}
						guid={this.props.fileResult.guid}
						name={this.props.fileResult.values[0]}
						mimeType={this.props.fileResult.mimeType}
						testName="fileLink"
					/>
				</td>
				<td>
					<span className="tinytool__info">
						{Math.ceil(this.props.fileResult.size / 1024)} kB
					</span>
				</td>
				<td>
					<DownloadButtonListTiny
						onClick={this.handleRequestDownload}
						switchState={this.props.fileResult.status}
					/>
				</td>
				{AppStore.context.sessionDetails.isDocumentManager &&
				!AppStore.context.sessionDetails.isApp ? (
					<td className="short-cell">
						<EditButtonListTiny
							onClick={this.handleEditDocument}
							testName="editButton"
						/>
					</td>
				) : null}
			</tr>
		);
	}
}
